import {ofType} from 'redux-observable';
import {Observable} from 'rxjs';
import i18next from 'i18next';
import {flatMap} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import * as PeopleActions from 'web-app/react/institution-people/people/actions';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import ChildBehaviors from 'web-app/behaviors/child-behavior-ids';
import ChildrenEntity from 'web-app/react/entities/children';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {defaultDuration} from 'web-app/react/global-event/helpers/constants';

import * as Api from './api';
import * as Actions from './actions';

export const createChildObservable = (
    institutionId,
    name,
    startDate,
    birthday,
    groupId,
    gender,
    createRelationForLogin = false,
    extraInfo = null,
    endDate,
    language,
    nationality,
    birthplace,
    suppressErrors = false,
) => {
    return Api.createChild(
        institutionId,
        name,
        startDate,
        birthday,
        groupId,
        gender,
        createRelationForLogin,
        extraInfo,
        endDate,
        language,
        nationality,
        birthplace,
        suppressErrors,
    ).map(child => Actions.createChildSuccess.action({child}));
};

export const fetchChildObservable = childId => {
    return Api.fetch(childId).map(({child, napsSummary}) => Actions.fetchSuccess.action(childId, child, napsSummary));
};

export const fetchChildrenObservable = (
    institutionId,
    embedRelations,
    includeFuture,
    includePast,
    overrideAll = false,
    groupIds,
    tagIds,
    alwaysShowPickup,
) => {
    return Api.fetchChildren(
        institutionId,
        embedRelations,
        includeFuture,
        includePast,
        groupIds,
        tagIds,
        alwaysShowPickup,
    ).map(children => Actions.fetchChildrenSuccess.action({children, overrideAll}));
};

export const updateBasicChildInfoObservable = (childId, child) =>
    Api.updateBasicChildInfo(childId, child).map(child => Actions.updateBasicChildInfoSuccess.action({child}));

export const fetchBasicChildInfoObservable = childId =>
    Api.fetchBasicChildInfo(childId).map(child => Actions.fetchBasicChildInfoSuccess.action(child));

const createChild = action$ =>
    action$
        .ofType(Actions.createChild.type)
        .mergeMap(({payload}) =>
            createChildObservable(
                payload.institutionId,
                payload.name,
                payload.startDate,
                payload.birthday,
                payload.groupId,
                payload.gender,
            ).catch(e => Observable.of(Actions.createChildFailed.action(e))),
        );

const updateBasicChildInfo = action$ =>
    action$
        .ofType(Actions.updateBasicChildInfo.type)
        .mergeMap(({payload}) =>
            updateBasicChildInfoObservable(payload.child.childId, payload.child).catch(e =>
                Observable.of(Actions.updateBasicChildInfoFailed.action(e)),
            ),
        );

const fetchBasicChildInfo = action$ =>
    action$
        .ofType(Actions.fetchBasicChildInfo.type)
        .mergeMap(({payload}) =>
            fetchBasicChildInfoObservable(payload.childId).catch(e =>
                Observable.of(Actions.fetchBasicChildInfoFailed.action(e)),
            ),
        );

const updateGroup = action$ =>
    action$.ofType(Actions.updateGroup.type).mergeMap(({payload}) =>
        Api.updateGroup(payload.childId, payload.groupId)
            .flatMap(child => [Actions.updateGroupSuccess.action({child})])
            .catch(e => Observable.of(Actions.updateGroupFailed.action({childId: payload.childId, error: e}))),
    );

export const removeTagObservable = (childId, tagId) =>
    Api.removeTag(childId, tagId).map(() => Actions.removeTagSuccess.action(childId, tagId));

export const addTagObservable = (childId, tagId) =>
    Api.addTag(childId, tagId).map(() => Actions.addTagSuccess.action(childId, tagId));

const addTag = action$ =>
    action$
        .ofType(Actions.addTag.type)
        .mergeMap(({payload}) =>
            addTagObservable(payload.childId, payload.tagId).catch(e =>
                Observable.of(Actions.addTagFailed.action({childId: payload.childId, error: e})),
            ),
        );

const removeTag = action$ =>
    action$
        .ofType(Actions.removeTag.type)
        .mergeMap(({payload}) =>
            removeTagObservable(payload.childId, payload.tagId).catch(e =>
                Observable.of(Actions.removeTagFailed.action({childId: payload.childId, error: e})),
            ),
        );

export const updateTagsObservable = (childId, enableTagIds, disableTagIds) =>
    Observable.zip(
        ...(enableTagIds ? enableTagIds.map(tagId => Api.addTag(childId, tagId)) : []),
        ...(disableTagIds ? disableTagIds.map(tagId => Api.removeTag(childId, tagId)) : []),
    ).map(() => Actions.updateTagsSuccess.action(childId, enableTagIds, disableTagIds));

const updateTags = action$ =>
    action$
        .ofType(Actions.updateTags.type)
        .mergeMap(({payload}) => updateTagsObservable(payload.childId, payload.enableTagIds, payload.disableTagIds))
        .catch(e => Observable.of(Actions.updateTagsFailed.action(e)));

const fetch = action$ =>
    action$
        .ofType(Actions.fetch.type)
        .mergeMap(({payload}) =>
            fetchChildObservable(payload.childId).catch(e => Observable.of(Actions.fetchFailed.action(e))),
        );

const fetchChildren = action$ =>
    action$
        .ofType(Actions.fetchChildren.type)
        .mergeMap(({payload}) =>
            fetchChildrenObservable(
                payload.institutionId,
                payload.embedRelations,
                payload.includeFuture,
                payload.includePast,
                payload.overrideAll,
                payload.groupIds,
                payload.tagIds,
                payload.alwaysShowPickup,
            ).catch(e => Observable.of(Actions.fetchChildrenFailed.action(e))),
        );

export const updateKeyWorkersObservable = (childId, primaryEmployeeId, secondaryEmployeeId) => {
    const apiPayload = [
        primaryEmployeeId
            ? {
                  status: 'PRIMARY',
                  employeeId: primaryEmployeeId,
              }
            : null,
        secondaryEmployeeId
            ? {
                  status: 'SECONDARY',
                  employeeId: secondaryEmployeeId,
              }
            : null,
    ].filter(kw => kw);
    return Api.updateKeyWorkers(childId, apiPayload).map(keyWorkers =>
        Actions.updateKeyWorkersSuccess.action(childId, keyWorkers),
    );
};

export const fetchKikuChildObservable = childId =>
    Api.getKikuChild(childId).map(kikuChild => Actions.fetchKikuChildSuccess.action(childId, kikuChild));

export const updateKikuChildObservable = (childId, kikuChild) =>
    Api.updateKikuChild(childId, kikuChild).map(kikuChild => Actions.updateKikuChildSuccess.action(childId, kikuChild));

const updateKeyWorkers = action$ =>
    action$
        .ofType(Actions.updateKeyWorkers.type)
        .mergeMap(({payload}) =>
            updateKeyWorkersObservable(payload.childId, payload.primaryEmployeeId, payload.secondaryEmployeeId).catch(
                e => Observable.of(Actions.updateKeyWorkersFailed.action(e)),
            ),
        );

export const checkInObservable = (childId, groupId, showWarningOnCheckIn) =>
    Api.checkIn(childId, groupId)
        .flatMap(response => {
            return [
                Actions.checkInSuccess.action(childId, response.checkinTime),
                showWarningOnCheckIn
                    ? GlobalEventActions.updateInfo.action(
                          i18next.t('group_tag.checkInNotExpected'),
                          false,
                          defaultDuration,
                          'checkin_not_expected',
                      )
                    : null,
            ].filter(hasValue);
        })
        .mergeMap(action => Observable.zip(Observable.of(action), fetchChildObservable(childId)));

const checkIn = action$ =>
    action$.ofType(Actions.checkIn.type).mergeMap(({payload}) =>
        checkInObservable(payload.childId, payload.groupId, payload.showWarningOnCheckIn)
            .flatMap(actions => actions)
            .catch(e => Observable.of(Actions.checkInFailed.action(e))),
    );

export const checkOutObservable = (childId, groupId) =>
    Api.checkOut(childId, groupId)
        .map(() => Actions.checkOutSuccess.action(childId))
        .mergeMap(action => Observable.zip(Observable.of(action), fetchChildObservable(childId)));

const checkOut = action$ =>
    action$.ofType(Actions.checkOut.type).mergeMap(({payload}) =>
        checkOutObservable(payload.childId)
            .flatMap(actions => actions)
            .catch(e => Observable.of(Actions.checkOutFailed.action(e))),
    );

const checkInNotExpected = (action$, state$) =>
    action$.pipe(
        ofType(PeopleActions.checkinSuccess.type),
        flatMap(({payload}) => {
            const notExpectedChildrenActions = (
                payload.childrenIds?.map(childId => {
                    const child = ChildrenEntity.selectors.getById(state$.value, {id: childId});
                    const {behaviors, name} = child;

                    if (behaviors && behaviors.includes(ChildBehaviors.ShowWarningOnChildCheckin)) {
                        return GlobalEventActions.updateInfo.action(
                            i18next.t('group_tag.checkInNotExpected', {
                                name: name.fullName,
                            }),
                        );
                    }
                    return null;
                }) ?? []
            ).filter(c => c);

            return notExpectedChildrenActions;
        }),
    );

export const fetchAutoGeneratedChildIdObservable = childId =>
    Api.getAutoGeneratedChildId(childId).map(response =>
        Actions.fetchAutoGeneratedChildIdSuccess.action(childId, response.ids[childId]),
    );

export const updateAutoGeneratedChildIdObservable = (childId, id) =>
    Api.updateAutoGeneratedChildId(childId, id).map(response =>
        Actions.updateAutoGeneratedChildIdSuccess.action(childId, response.ids[childId]),
    );

export default combineEpics(
    createChild,
    updateGroup,
    fetchChildren,
    updateBasicChildInfo,
    fetchBasicChildInfo,
    fetch,
    updateKeyWorkers,
    addTag,
    removeTag,
    updateTags,
    checkIn,
    checkOut,
    checkInNotExpected,
);
